import { flow, types } from 'mobx-state-tree';
import {
  RoleNames,
  TeamMemberDtoModelType,
  UserInviteDtoModelType,
} from './models';
import getAppRoot from './utils/getAppRoot';
import MemberPaginatedQuery from './queries/team/TeamMemberPaginatedQuery';
import PendingMemberQuery from './queries/team/PendingMemberQuery';
import { UserInviteStatus } from './Router/constants';

const PAGE_SIZE = 25;

export interface RoleInfo {
  id: string;
  name: string;
}

export interface UnifiedTeamMember {
  isPending: boolean;
  uuid?: string;
  email?: string | null;
  fullname?: string | null;
  expiresOn?: string | null;
  roles: RoleInfo[];
}

const Team = types.model('Team')
  .props({
    isLoading: types.optional(types.boolean, false),
    hasError: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get userInvites(): UnifiedTeamMember[] {
      return Array.from<UserInviteDtoModelType>(getAppRoot(self).model.userInviteDtos.values())
        .map((ui: UserInviteDtoModelType) => ({
          isPending: UserInviteStatus.Pending == ui.inviteStatusId,
          uuid: ui.uuid,
          email: ui.email,
          roles: ui.role?.id && ui.role?.name ? [{ id: ui.role.id, name: ui.role.name }] : [],
          expiresOn: ui.expiresOn || null,
          createdOn: ui.createdOn || '',
        }))
        .sort((a, b) => {
          const dateA = new Date(a.createdOn).getTime();
          const dateB = new Date(b.createdOn).getTime();
          return dateB - dateA;
        });
    },
    get pendingUserInvites(): UnifiedTeamMember[] {
      return this.userInvites.filter((invite) => {
        return !!(invite.isPending && invite.email !== getAppRoot(self).account.user?.email);
      });
    },
    get allTeamMembers(): UnifiedTeamMember[] {
      return Array.from<TeamMemberDtoModelType>(getAppRoot(self).model.teamMemberDtos.values()).map((u: TeamMemberDtoModelType) => ({
        isPending: false,
        uuid: u.uuid,
        email: u.email,
        fullname: u.fullname,
        roles: u.roles
          .filter((r) => r.id && r.name)
          .map((r) => ({ id: r.id, name: r.name as string })),
      }));
    },
    get membersAndPendingInvites(): UnifiedTeamMember[] {
      return Array.from([...this.pendingUserInvites, ...this.allTeamMembers]);
    },
  }))
  .actions((self) => ({
    fetchTeamMembers: flow(function* () {
      const fetchPendingInvites = async () => {
        const res = await getAppRoot(self).model.queryUserInvites({}, PendingMemberQuery, { fetchPolicy: 'no-cache' }).currentPromise();
        return res.userInvites;
      };

      const fetchExistingMembersPaginated = async (offset: number): Promise<any> => {
        const teamMembersPaginatedResponse = await new Promise<any>((resolve, reject) => {
          getAppRoot(self).model.queryTeamMembersPaginated({
            offset,
            limit: PAGE_SIZE,
          }, MemberPaginatedQuery, { fetchPolicy: 'no-cache' }).currentPromise().then((pageResponse) => {
            resolve(pageResponse);
          }).catch((err) => {
            reject(err);
          });
        });

        if (teamMembersPaginatedResponse.teamMembersPaginated.hasNextPage) {
          return fetchExistingMembersPaginated(offset + PAGE_SIZE);
        }
      };

      try {
        getAppRoot(self).model.clearTeamMembers();
        self.isLoading = true;
        self.hasError = false;
        yield fetchPendingInvites();
        yield fetchExistingMembersPaginated(0);
      } catch {
        self.hasError = true;
        return Promise.reject();
      } finally {
        self.isLoading = false;
      }

      return Promise.resolve();
    }),
    createUserInvite: flow(function* (email: string, role: RoleNames) {
      const res = yield getAppRoot(self).model.mutateCreateUserInvite({ email, role }).currentPromise();
      return res.createUserInvite;
    }),
    resendUserInvite: flow(function* (inviteUuid: string) {
      const res = yield getAppRoot(self).model.mutateResendUserInvite({ inviteUuid }).currentPromise();
      return res.resendUserInvite;
    }),
    cancelUserInvite: flow(function* (inviteUuid: string) {
      return yield getAppRoot(self).model.mutateRevokeUserInvite({ inviteUuid }).currentPromise();
    }),
    updateUserRoles: flow(function* (userUuid: string, roleIds: string[]) {
      // Convert string[] to number[] for the GraphQL mutation
      const numericRoleIds = roleIds.map((id) => parseInt(id, 10));

      const res = yield getAppRoot(self).model.mutateUpdateUserRoles(
        { userUuid, roleIds: numericRoleIds },
      ).currentPromise();

      return res.updateUserRoles;
    }),
    deleteTeamMember: flow(function* (userUuid: string) {
      const res = yield getAppRoot(self).model.mutateDeleteTeamMember({ userUuid }).currentPromise();

      if (res.deleteTeamMember?.success) {
        return res.deleteTeamMember;
      }
      throw new Error('Delete team member failed');
    }),
    setIsLoading(loading: boolean) {
      self.isLoading = loading;
    },
  }));

export default Team;
